import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import EditDriveItems from "@/views/EditDriveItems.vue";
import { MimeType, TaskType, ApiResult } from "../../enums";
import { Consts } from '@/consts';
import { GraphApiUtils } from '@/utils/graphApiUtils';
import { AppConfig } from '@/app-config';
import { PlannerUtils } from '@/utils/plannerUtils';
import { Bucket } from "../../models/bucket";
import { Buckets } from '@/models/buckets';
import { Plan } from '@/models/plan';
import BucketItem from "@/components/BucketItem.vue";
import Loading from '@/components/Loading.vue';
import { Channel } from '@/models/channel';
import { Plans } from '@/models/plans';
import { Channels } from '@/models/channels';
import { Tasks } from '@/models/tasks';
import { DateUtils } from '@/utils/dateUtils';
import { Task } from '@/models/task';
import DriveItemsButton from '@/components/DriveItemsButton';
import { PathUtils } from '@/utils/pathUtils';

@Component({
	components: {
		BucketItem,
		EditDriveItems,
		Loading
	}
})

export default class UserChannel extends Vue {
	@Prop() public channels!: Channels;
	@Prop() public plans!: Plans;

	private userId: string = '';
	private userName: string = '';
	private channel: Channel | null = null;
	private plan: Plan | null = null;
	private buckets: Buckets = new Buckets();
	private tasks: Tasks = new Tasks();
	private bucket: Bucket | null = null;
	private subTitle: string = "カリキュラム内容";

	private loading: boolean = true;
	private editDriveItemsVisible: boolean = false;
	private editDriveItemsTitle: string = '';
	private editDriveItemsPath: string = '';
	private editDriveItemsMimeType: MimeType = MimeType.All;
	private deleteButtonVisible: boolean = false;
	private downloadButtonInvisible: boolean = false;
	private openBrowserButtonInvisible: boolean = false;
	private uploadButtonVisible: boolean = false;

	public async created(): Promise<void> {
		// アクセストークンが有効でない場合はルートに遷移。
		if (!await GraphApiUtils.isValidAccessToken(Vue.$accessTokenInfo)) {
			this.$router.push(Consts.PATH_USER);
			return;
		}
		const userId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_ID);
		if (userId !== null) {
			this.userId = userId;
		}
		const userName: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_DISPLAYNAME);
		if (userName !== null) {
			this.userName = userName;
		}
		await this.update();
	}

	// TODO channelsの更新とplansの更新が同時に行われていることを前提にしているこの処理はNG
	@Watch('channels')
	private async onChannelsChange(newChannels: Channels, oldChannels: Channels): Promise<void> {
		await this.update();
	}

	private async update(): Promise<void> {
		this.buckets = new Buckets();
		this.tasks = new Tasks();
		const channelId: string = this.$route.params.id;
		const channel: Channel | null = this.getChannel(channelId);
		if (channel === null) {
			return;
		}
		// プランの取得
		const plan: Plan | null = this.getPlan(channel.displayName);
		if (plan === null) {
			return;
		}
		// バケット一覧の取得
		const accessToken: string = Vue.$accessTokenInfo.token;
		const buckets: Buckets | null = await this.getBuckets(accessToken, plan.id);
		if (buckets === null) {
			return;
		}
		const tasks: Tasks | null = await this.getTasks(accessToken, plan.id);
		if (tasks === null) {
			return;
		}
		this.channel = channel;
		this.plan = plan;
		this.tasks = tasks; // buckets以前に設定しておく
		this.buckets = buckets;
		this.loading = false;
	}

	private getDriveItemsButtons(bucketName: string): DriveItemsButton[] {
		if (this.channel === null) {
			return [];
		}
		const driveItemsButtons: DriveItemsButton[] = [];
		switch (bucketName) {
			case AppConfig.BUCKET_NAME_PRETASK:
			case AppConfig.BUCKET_NAME_PRETASK2:
			case AppConfig.BUCKET_NAME_PRETASK3:
			case AppConfig.BUCKET_NAME_POSTTASK: {
				const button1 = new DriveItemsButton();
				button1.title = '課題を確認';
				button1.path = PathUtils.GetAdminBucketPath(this.channel.id, bucketName);
				driveItemsButtons.push(button1);
				const button2 = new DriveItemsButton();
				button2.title = 'レポートを提出';
				button2.path = PathUtils.GetUserSubmitReportPath(this.userId, this.userName, this.channel.id, bucketName);
				driveItemsButtons.push(button2);
				const button3 = new DriveItemsButton();
				button3.title = '採点結果を確認';
				button3.path = PathUtils.GetUserCheckScorePath(this.userId, this.userName, this.channel.id, bucketName);
				driveItemsButtons.push(button3);
				break;
			}
			case AppConfig.BUCKET_NAME_LECTURE: {
				const button1 = new DriveItemsButton();
				button1.title = '資料一覧';
				button1.path = PathUtils.GetAdminBucketPath(this.channel.id, bucketName);
				button1.mimeType = MimeType.NotVideo;
				driveItemsButtons.push(button1);
				const button2 = new DriveItemsButton();
				button2.title = '動画一覧';
				button2.path = PathUtils.GetAdminBucketPath(this.channel.id, bucketName);
				button2.mimeType = MimeType.Video;
				driveItemsButtons.push(button2);
				break;
			}
		}
		return driveItemsButtons;
	}

	private getBucketItemDateString(bucketId: string, bucketName: string): string {
		if ((this.plan === null) || (this.tasks === null)) {
			return '';
		}
		const bucketTasks: Tasks = this.tasks.getBucketTasks(this.plan.id, bucketId);
		switch (bucketName) {
			case AppConfig.BUCKET_NAME_PRETASK:
			case AppConfig.BUCKET_NAME_PRETASK2:
			case AppConfig.BUCKET_NAME_PRETASK3:
			case AppConfig.BUCKET_NAME_POSTTASK: {
				const myTasks: Tasks = bucketTasks.getMyTasks(this.userId);
				const task: Task | null = myTasks.getByTitle(AppConfig.USER_TASK_NAME_SUBMIT_REPORT);
				if ((task === null) || (task.dueDateTime === null)) {
					return '';
				}
				const formattedDate: string = DateUtils.GetStringWithSlash(task.dueDateTime);
				const date: string = `提出期限：${formattedDate}`;
				return date;
			}
			case AppConfig.BUCKET_NAME_LECTURE: {
				const task = bucketTasks.getByTitle(AppConfig.ADMIN_TASK_NAME_GIVE_LECTURE);
				if ((task === null) || (task.dueDateTime === null)) {
					return '';
				}
				const formattedDate: string = DateUtils.GetStringWithSlash(task.dueDateTime);
				const date: string = `講義日：${formattedDate}`;
				return date;
			}
		}
		return '';
	}

	private getBucketItemDisabled(bucketId: string, bucketName: string): boolean {
		if ((this.plan === null) || (this.tasks === null)) {
			return true;
		}
		const bucketTasks: Tasks = this.tasks.getBucketTasks(this.plan.id, bucketId);
		switch (bucketName) {
			case AppConfig.BUCKET_NAME_PRETASK:
			case AppConfig.BUCKET_NAME_PRETASK2:
			case AppConfig.BUCKET_NAME_PRETASK3:
			case AppConfig.BUCKET_NAME_POSTTASK: {
				const myTasks: Tasks = bucketTasks.getMyTasks(this.userId);
				const task: Task | null = myTasks.getByTitle(AppConfig.USER_TASK_NAME_SUBMIT_REPORT);
				if ((task === null) || (task.dueDateTime === null)) {
					return true;
				}
				return false;
			}
			case AppConfig.BUCKET_NAME_LECTURE: {
				const task = bucketTasks.getByTitle(AppConfig.ADMIN_TASK_NAME_GIVE_LECTURE);
				if ((task === null) || (task.dueDateTime === null)) {
					return true;
				}
				return false;
			}
		}
		return true;
	}

	private getChannel(channelId: string): Channel | null {
		if (this.channels === undefined) {
			return null;
		}
		const channel: Channel | null = this.channels.get(channelId);
		return channel;
	}

	private getPlan(channelName: string): Plan | null {
		if (this.plans === undefined) {
			return null;
		}
		const plan: Plan | null = this.plans.getByTitle(channelName);
		return plan;
	}

	private async getBuckets(accessToken: string, planId: string): Promise<Buckets | null> {
		const bucketsResult = await PlannerUtils.GetBuckets(accessToken, planId);
		if ((bucketsResult.result !== ApiResult.Success) || (bucketsResult.value === null)) {
			alert('バケット一覧の取得に失敗しました');
			return null;
		}
		const buckets: Buckets = bucketsResult.value;
		buckets.sort();
		return buckets;
	}

	private async getTasks(accessToken: string, planId: string): Promise<Tasks | null> {
		const tasksResult = await PlannerUtils.GetTasks(accessToken, planId);
		if ((tasksResult.result !== ApiResult.Success) || (tasksResult.value === null)) {
			alert('タスク一覧の取得に失敗しました');
			return null;
		}
		const tasks: Tasks | null = tasksResult.value;
		return tasks;
	}

	private onBucketItemClick(bucketId: string): void {
		const bucket: Bucket | null = this.buckets.get(bucketId);
		if (bucket === null) {
			return;
		}
	}

	private onDriveItemsButtonClick(bucketId: string, driveItemsPath: string | undefined, mimeType: MimeType): void {
		const bucket: Bucket | null = this.buckets.get(bucketId);
		if (bucket === null) {
			return;
		}
		if (driveItemsPath === undefined) {
			return;
		}
		let title: string = '';
		this.deleteButtonVisible = false;
		this.downloadButtonInvisible = false;
		this.openBrowserButtonInvisible = false;
		this.uploadButtonVisible = false;
		if (driveItemsPath.indexOf(`/${AppConfig.USER_ROOT_FOLDER_NAME}`) > -1) {
			if (driveItemsPath.indexOf(`/${AppConfig.USER_TASK_FOLDERNAME_SUBMITREPORT}`) > -1) {
				// /生徒/***/提出レポート
				title = `${bucket.name} ${AppConfig.USER_TASK_NAME_SUBMIT_REPORT}`;
				this.uploadButtonVisible = true;
				this.deleteButtonVisible = true;
			} else if (driveItemsPath.indexOf(`/${AppConfig.USER_TASK_FOLDERNAME_CHECKSCORE}`) > -1) {
				// /生徒/***/採点結果
				title = `${bucket.name} ${AppConfig.USER_TASK_NAME_CHECK_SCORE}`;
			}
		} else if (driveItemsPath.indexOf(`/${AppConfig.ADMIN_ROOT_FOLDER_NAME}`) > -1) {
			if ((driveItemsPath.indexOf(`/${AppConfig.BUCKET_NAME_PRETASK}`) > -1) ||
				(driveItemsPath.indexOf(`/${AppConfig.BUCKET_NAME_POSTTASK}`) > -1)) {
				// /運営/事前課題**もしくは/運営/事後課題
				title = `${bucket.name} ${AppConfig.USER_TASK_NAME_CHECK_TASK}`;
				this.openBrowserButtonInvisible = true;
			} else if (driveItemsPath.indexOf(`/${AppConfig.BUCKET_NAME_LECTURE}`) > -1) {
				// /運営/講義
				if (mimeType === MimeType.NotVideo) {
					// 資料一覧
					title = `${bucket.name} ${AppConfig.USER_TASK_NAME_CHECK_DOCS}`;
					this.openBrowserButtonInvisible = true;
				} else if (mimeType === MimeType.Video) {
					// 動画一覧
					title = `${bucket.name} ${AppConfig.USER_TASK_NAME_CHECK_VIDEOS}`;
					this.downloadButtonInvisible = true;
				}
			}
		}
		// TODO もっとスマートに取得したい
		this.editDriveItemsTitle = title;
		this.editDriveItemsPath = driveItemsPath;
		this.editDriveItemsMimeType = mimeType;
		this.bucket = bucket;
		this.editDriveItemsVisible = true;
	}

	private onEditDriveItemsClose(): void {
		this.editDriveItemsVisible = false;
	}

}
